import rt from "aldoo-ra/Realtime"

/**
 * Realtime Task Progress Controller
 * Provides real-time task updates via Socket.IO
 */
function createRealtimeTaskController() {
  let taskSocket = null
  let isInitialized = false
  let taskSubscriptions = new Map() // taskId -> Set of callbacks

  // Initialize socket connection
  const initialize = () => {
    if (isInitialized) return taskSocket

    try {
      // Connect to /task namespace
      taskSocket = rt('task')
      isInitialized = true

      // Setup event listeners
      setupEventListeners()

      return taskSocket
    } catch (error) {
      console.error('❌ Task RT: Failed to connect:', error)
      throw error
    }
  }

  // Setup Socket.IO event listeners
  const setupEventListeners = () => {
    if (!taskSocket) return

    // Task started
    taskSocket.on('task:started', (data) => {
      notifySubscribers(data.id, data)
    })

    // Task update/progress
    taskSocket.on('task:update', (data) => {
      notifySubscribers(data.id, data)
    })

    // Task completed
    taskSocket.on('task:completed', (data) => {
      notifySubscribers(data.id, data)
    })

    // Connection events
    taskSocket.on('connect', () => {
      // Connected
    })

    taskSocket.on('disconnect', (reason) => {
      // Disconnected
    })

    taskSocket.on('connect_error', (error) => {
      console.error('❌ Task RT: Connection error:', error)
    })
  }

  // Notify all subscribers for a task
  const notifySubscribers = (taskId, data) => {
    const subscribers = taskSubscriptions.get(taskId)
    if (subscribers) {
      subscribers.forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error('❌ Task RT: Error in subscriber callback:', error)
        }
      })
    }
  }

  // Subscribe to a task's updates
  const subscribe = (taskId, callback) => {
    if (!taskSocket) {
      initialize()
    }

    // Add callback to subscriptions
    if (!taskSubscriptions.has(taskId)) {
      taskSubscriptions.set(taskId, new Set())
    }
    taskSubscriptions.get(taskId).add(callback)

    // Subscribe to task room on server
    taskSocket.emit('task:subscribe', { taskId }, (response) => {
      if (response.error) {
        console.error(`❌ Task RT: Failed to subscribe to task ${taskId}:`, response.error)
      } else {
        // Send initial task data to callback
        if (response.task) {
          callback(response.task)
        }
      }
    })

    // Return unsubscribe function
    return () => unsubscribe(taskId, callback)
  }

  // Unsubscribe from a task's updates
  const unsubscribe = (taskId, callback) => {
    const subscribers = taskSubscriptions.get(taskId)
    if (subscribers) {
      subscribers.delete(callback)

      // If no more subscribers, unsubscribe from server
      if (subscribers.size === 0) {
        taskSubscriptions.delete(taskId)
        if (taskSocket) {
          taskSocket.emit('task:unsubscribe', { taskId }, (response) => {
            if (response.error) {
              console.error(`❌ Task RT: Failed to unsubscribe from task ${taskId}:`, response.error)
            }
          })
        }
      }
    }
  }

  // Get task status
  const getTask = (taskId) => {
    return new Promise((resolve, reject) => {
      if (!taskSocket) {
        initialize()
      }

      taskSocket.emit('task:get', { taskId }, (response) => {
        if (response.error) {
          reject(new Error(response.error))
        } else {
          resolve(response.task)
        }
      })
    })
  }

  // List all tasks
  const listTasks = () => {
    return new Promise((resolve, reject) => {
      if (!taskSocket) {
        initialize()
      }

      taskSocket.emit('task:list', {}, (response) => {
        if (response.error) {
          reject(new Error(response.error))
        } else {
          resolve(response.tasks)
        }
      })
    })
  }

  return {
    initialize,
    subscribe,
    unsubscribe,
    getTask,
    listTasks,
    get socket() {
      return taskSocket
    },
    get isConnected() {
      return taskSocket?.connected || false
    }
  }
}

// Singleton instance
let controllerInstance = null

export default function getRealtimeTaskController() {
  if (!controllerInstance) {
    controllerInstance = createRealtimeTaskController()
  }
  return controllerInstance
}
